
import { useEffect, useState } from "react";
import { Modal, Spinner, Alert } from "react-bootstrap";
import axios from "axios";

const getLocalData = () => {
  try {
    return JSON.parse(
      localStorage.getItem("kt-auth-react-v") || "null"
    );
  } catch {
    return null;
  }
};

const trackingFields = [
  { key: "tracking_no", label: "Tracking Number", icon: "bi-upc-scan" },
  { key: "tracking_date", label: "Tracking Created On", icon: "bi-calendar-check" },
  { key: "scan_date", label: "Tracking Scanned On", icon: "bi-upc" },
  { key: "statement_no", label: "Statement Generated", icon: "bi-file-earmark-text" },
  { key: "withdraw_no", label: "Payment Number", icon: "bi-receipt" },
  { key: "payment_mode", label: "Payment Mode", icon: "bi-credit-card" },
  { key: "payment_date", label: "Payment Date", icon: "bi-calendar-check" },
];

export default function OrderInfo({ show, orderId, onHide }) {
  const API_URL = process.env.REACT_APP_API_URL;
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orderNo,setOrderNo] = useState("");

  useEffect(() => {
    if (!show || !orderId) return;

    const controller = new AbortController();

    const fetchTracking = async () => {
      setLoading(true);
      setError("");
      setTracking(null);

      try {
        const token = getLocalData()?.api_token;
        if (!token) throw new Error("Authentication token not found.");

        const response = await axios.get(
          `${API_URL}/user/order/info`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { order_id: orderId },
            signal: controller.signal,
          }
        );

        setTracking(response.data?.data ?? response.data);
        setOrderNo(response.data.data?.order_no);
      } catch (err) {
        if (err.code !== "ERR_CANCELED") {
          setError(
            err.response?.data?.message ||
            err.message ||
            "Unable to load order information."
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchTracking();
    return () => controller.abort();
  }, [show, orderId, API_URL]);

  const items = trackingFields.filter(({ key }) => {
    const value = tracking?.[key];

    return (
      value !== null &&
      value !== undefined &&
      String(value).trim() !== "" &&
      !(
        key === "statement_no" &&
        String(value).trim().toLowerCase() === "statement not found"
      )
    );
  });

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="sm"
      dialogClassName="order-info-modal"
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="order-modal-title">
           {orderNo}
          </Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
            <div className="small text-muted mt-2">Loading...</div>
          </div>
        )}

        {!loading && error && (
          <Alert variant="danger" className="small mb-0 py-2">
            {error}
          </Alert>
        )}

        {!loading && !error && tracking && items.length > 0 && (
          <div className="compact-timeline">
            {items.map((item, index) => (
              <div className="compact-timeline-item" key={item.key}>
                <div className="compact-timeline-rail">
                  <span className="compact-timeline-dot">
                    <i className={`bi ${item.icon} text-white fs-4`} />
                  </span>

                  {index !== items.length - 1 && (
                    <span className="compact-timeline-line" />
                  )}
                </div>

                <div className="compact-timeline-content">
                  <div className="compact-timeline-label">
                    {item.label}
                  </div>
                  <div className="compact-timeline-value">
                    {String(tracking[item.key])}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && tracking && items.length === 0 && (
          <div className="text-center text-muted small py-3">
            No tracking information available.
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
